import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { Add, AssignmentTurnedIn, LocationOn, PictureAsPdf, Print, Save, Visibility } from "@mui/icons-material";
import {
  createDailyClosure,
  createRecovery,
  fetchAccounts,
  fetchClientFiche,
  fetchClients,
  fetchDailyClosureDetails,
  fetchDailyClosures,
  fetchInvestments,
  fetchRecoveries,
  fetchUsers,
  validateDailyClosure,
} from "../services/owdedApi";
import { dateOnly, money } from "./format";
import { EmptyState, LoadingOverlay, Modal, Pagination, StatusPill } from "./ui";
import { currentMonthRange, paginateRows, PAGE_SIZE, rangeForMode, totalsByCurrency } from "./listUtils";
import { BASE_URL } from "../services/constant";
import { useAuth } from "../context/AuthContext";
import { canOperateRecovery, hasManagementAccess } from "../utils/access";

const initialForm = {
  investment_id: "",
  amount_paid: 0,
  cash_account_id: "",
  latitude: "",
  longitude: "",
  observation: "",
};

function RecoveriesPage() {
  const { user } = useAuth();
  const [recoveries, setRecoveries] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [closures, setClosures] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [search, setSearch] = useState("");
  const [periodMode, setPeriodMode] = useState("MONTH");
  const [period, setPeriod] = useState(currentMonthRange());
  const [page, setPage] = useState(1);
  const [closurePage, setClosurePage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedAgentPortfolio, setSelectedAgentPortfolio] = useState(null);
  const [selectedClientFiche, setSelectedClientFiche] = useState(null);
  const [closureDetails, setClosureDetails] = useState(null);
  const [closureReportOpen, setClosureReportOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reportMeta, setReportMeta] = useState({ day: "", dateLabel: "", rubric: "", agentName: "" });
  const [notice, setNotice] = useState("");
  const reportRef = useRef(null);
  const closureReportRef = useRef(null);
  const canRecordRecovery = canOperateRecovery(user);
  const canValidateClosure = hasManagementAccess(user);
  const activeRange = useMemo(
    () => rangeForMode(periodMode, period.start_date, period.end_date),
    [periodMode, period.start_date, period.end_date]
  );

  const assetUrl = (url) => {
    if (!url) return "";
    return url.startsWith("http") ? url : `${BASE_URL}${url}`;
  };

  const load = async () => {
    const [recoveryRows, investmentRows, accountRows, closureRows, userRows, clientRows] = await Promise.all([
      fetchRecoveries(activeRange),
      fetchInvestments(),
      fetchAccounts().catch(() => []),
      fetchDailyClosures(activeRange).catch(() => []),
      fetchUsers().catch(() => []),
      fetchClients().catch(() => []),
    ]);
    setRecoveries(recoveryRows);
    setInvestments(investmentRows);
    setAccounts(accountRows);
    setClosures(closureRows);
    setUsers(userRows);
    setClients(clientRows);
  };

  useEffect(() => {
    load().catch(() => setNotice("Impossible de charger les cotisations."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRange.start_date, activeRange.end_date]);

  const filtered = useMemo(() => {
    const term = search.toLowerCase();
    return recoveries.filter((recovery) =>
      [recovery.client_name, recovery.client_number, recovery.investment_reference, recovery.recovery_agent_name]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term))
    );
  }, [recoveries, search]);

  const pagination = useMemo(() => paginateRows(filtered, page, PAGE_SIZE), [filtered, page]);
  const closurePagination = useMemo(() => paginateRows(closures, closurePage, PAGE_SIZE), [closures, closurePage]);
  const totals = useMemo(() => totalsByCurrency(filtered, "amount_paid", "currency"), [filtered]);
  const closureTotal = useMemo(
    () => closures.reduce((acc, closure) => ({
      CDF: acc.CDF + Number(closure.total_collected_cdf || 0),
      USD: acc.USD + Number(closure.total_collected_usd || 0),
    }), { CDF: 0, USD: 0 }),
    [closures]
  );
  const recoveryInvestments = useMemo(
    () => investments.filter((investment) => ["VALIDE", "DECAISSE", "EN_RECOUVREMENT", "SOLDE"].includes(String(investment.status || "").toUpperCase())),
    [investments]
  );
  const rubricLabel = useMemo(() => {
    if (periodMode === "DAY") return "Journalier";
    if (periodMode === "WEEK") return `Hebdomadaire du ${dateOnly(activeRange.start_date)} au ${dateOnly(activeRange.end_date)}`;
    if (periodMode === "MONTH") {
      return `Mensuel - ${new Date(`${activeRange.start_date}T00:00:00`).toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}`;
    }
    return `Periodique du ${dateOnly(activeRange.start_date)} au ${dateOnly(activeRange.end_date)}`;
  }, [periodMode, activeRange.start_date, activeRange.end_date]);
  const dayLabel = useMemo(
    () => new Date(`${activeRange.start_date}T00:00:00`).toLocaleDateString("fr-FR", { weekday: "long" }),
    [activeRange.start_date]
  );

const getRecoveryCurrency = (row) => {
  return String(row.product_currency || row.currency || "CDF").toUpperCase();
};

const getRecoveryClientKey = (row) => {
  return row.client_id || row.client_number || row.client_name || "CLIENT_INCONNU";
};

const getRecoveryProductKey = (row) => {
  return (
    row.product_id ||
    row.investment_id ||
    row.collection_card_id ||
    row.investment_reference ||
    row.product_name ||
    row.amount_to_pay ||
    "PRODUIT_INCONNU"
  );
};

const getRecoveryRemainingKey = (row) => {
  return [
    getRecoveryClientKey(row),
    getRecoveryProductKey(row),
    getRecoveryCurrency(row),
  ].map((value) => String(value || "").trim()).join("|");
};

/* eslint-disable react-hooks/exhaustive-deps */
const uniqueRemainingTotalsByClientProduct = useCallback((rows) => {
  const uniqueMap = new Map();

  rows.forEach((row) => {
    const key = getRecoveryRemainingKey(row);
    const currency = getRecoveryCurrency(row);
    const remainingAmount = Number(row.remaining_amount || 0);
    const paidAt = row.paid_at ? new Date(row.paid_at).getTime() : 0;

    const existing = uniqueMap.get(key);

    /**
     * On garde une seule valeur du reste à payer
     * pour le même client + même produit.
     *
     * Si plusieurs lignes existent, on garde la plus récente,
     * car elle représente normalement le reste le plus à jour.
     */
    if (!existing || paidAt >= existing.paidAt) {
      uniqueMap.set(key, {
        currency,
        remainingAmount,
        paidAt,
      });
    }
  });

  return [...uniqueMap.values()].reduce(
    (acc, item) => {
      if (!acc[item.currency]) acc[item.currency] = 0;
      acc[item.currency] += item.remainingAmount;
      return acc;
    },
    { CDF: 0, USD: 0 }
  );
}, []);
/* eslint-enable react-hooks/exhaustive-deps */




  const makeReport = useCallback((key, agentName, rows, agentPhoto = "") => {
  const uniqueClients = new Set(
    rows.map((row) => row.client_id || row.client_number)
  ).size;

  return {
    key,
    agentName,
    agentPhoto,
    rows,
    clientsCount: uniqueClients,

    // Ici on cumule normalement les montants encaissés,
    // car chaque ligne représente un paiement réel.
    collectedTotals: totalsByCurrency(rows, "amount_paid", "currency"),

    // Ici on ne cumule plus le reste à payer ligne par ligne.
    // On prend une seule fois le reste pour le même client + même produit.
    remainingTotals: uniqueRemainingTotalsByClientProduct(rows),
  };
}, [uniqueRemainingTotalsByClientProduct]);

  const agentReports = useMemo(() => {
    const groups = filtered.reduce((acc, recovery) => {
      const key = recovery.agent_recovery_id || "UNASSIGNED";
      const agentName = recovery.recovery_agent_name || "Non affecte";
      if (!acc[key]) acc[key] = { agentName, agentPhoto: recovery.recovery_agent_photo_url, rows: [] };
      acc[key].rows.push(recovery);
      return acc;
    }, {});

    return [
      makeReport("ALL", "Tous les agents", filtered),
      ...Object.entries(groups).map(([key, group]) => makeReport(key, group.agentName, group.rows, group.agentPhoto)),
    ];
  }, [filtered, makeReport]);

  const agentPortfolios = useMemo(() => {
    const fieldUsers = users.filter((item) => ["AGENT_RECOUVREMENT", "AGENT_COMMERCIAL", "AGENT_CREDIT"].includes(item.role_code));

    return fieldUsers.map((agent) => {
      const id = Number(agent.id);
      const clientMap = new Map();

      clients.forEach((client) => {
        if (Number(client.commercial_agent_id || client.created_by || 0) === id) {
          clientMap.set(Number(client.id), client);
        }
      });

      investments.forEach((investment) => {
        if (Number(investment.agent_recovery_id || 0) === id || Number(investment.agent_commercial_id || 0) === id) {
          const client = clients.find((row) => Number(row.id) === Number(investment.client_id));
          if (client) clientMap.set(Number(client.id), client);
        }
      });

      const agentRecoveries = recoveries.filter((recovery) => Number(recovery.agent_recovery_id || 0) === id);

      return {
        agent,
        clients: [...clientMap.values()],
        investments: investments.filter((investment) => Number(investment.agent_recovery_id || 0) === id || Number(investment.agent_commercial_id || 0) === id),
        recoveries: agentRecoveries,
        collectedTotals: totalsByCurrency(agentRecoveries, "amount_paid", "currency"),
        totalCollected: agentRecoveries.reduce((sum, row) => sum + Number(row.amount_paid || 0), 0),
      };
    });
  }, [clients, investments, recoveries, users]);

  useEffect(() => {
    setPage(1);
    setClosurePage(1);
  }, [search, activeRange.start_date, activeRange.end_date]);

  const locate = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((position) => {
      setForm((current) => ({
        ...current,
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      }));
    });
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!canRecordRecovery) {
      setNotice("Seul un agent de recouvrement peut enregistrer une cotisation.");
      return;
    }
    setLoading(true);
    try {
      await createRecovery({
        ...form,
        cash_account_id: form.cash_account_id || null,
        latitude: form.latitude || null,
        longitude: form.longitude || null,
      });
      setForm(initialForm);
      setModalOpen(false);
      setNotice("Cotisation enregistree.");
      await load();
    } catch (err) {
      setNotice(err.response?.data?.error || "Impossible d'enregistrer la cotisation.");
    } finally {
      setLoading(false);
    }
  };

  const selectRecoveryInvestment = (investmentId) => {
    const investment = recoveryInvestments.find((item) => String(item.id) === String(investmentId));
    setForm((current) => ({
      ...current,
      investment_id: investmentId,
      amount_paid: investment?.expected_contribution_amount || investment?.amount_to_pay || current.amount_paid,
    }));
  };

  const closeDay = async () => {
    if (!canRecordRecovery) {
      setNotice("Seul un agent de recouvrement peut cloturer sa journee.");
      return;
    }
    setLoading(true);
    try {
      await createDailyClosure({ closure_date: new Date().toISOString().slice(0, 10) });
      setNotice("Journee cloturee et rapport soumis au manager.");
      await load();
    } catch (err) {
      setNotice(err.response?.data?.error || "Impossible de cloturer la journee.");
    } finally {
      setLoading(false);
    }
  };

  const openAgentPortfolio = (portfolio) => {
    setSelectedAgentPortfolio(portfolio);
    setSelectedClientFiche(null);
  };

  const openClientFiche = async (client) => {
    setLoading(true);
    setSelectedClientFiche(null);
    try {
      setSelectedClientFiche(await fetchClientFiche(client.id));
    } catch (err) {
      setNotice("Impossible de charger la fiche client.");
    } finally {
      setLoading(false);
    }
  };

  const openClosureReport = async (closure) => {
    setLoading(true);
    setClosureDetails(null);
    setClosureReportOpen(true);
    try {
      setClosureDetails(await fetchDailyClosureDetails(closure.id));
    } catch (err) {
      setNotice("Impossible de charger le rapport journalier.");
      setClosureReportOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const openReport = (report) => {
    setSelectedReport(report);
    setReportMeta({
      day: dayLabel,
      dateLabel: activeRange.start_date === activeRange.end_date
        ? dateOnly(activeRange.start_date)
        : `${dateOnly(activeRange.start_date)} - ${dateOnly(activeRange.end_date)}`,
      rubric: rubricLabel,
      agentName: report.agentName,
    });
    setReportOpen(true);
  };

  const buildRecoveryPdf = async () => {
    if (!reportRef.current || !selectedReport) return null;
    const canvas = await html2canvas(reportRef.current, {
      scale: 2,
      backgroundColor: "#ffffff",
      useCORS: true,
    });
    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 8;
    const imageWidth = pageWidth - margin * 2;
    const imageHeight = (canvas.height * imageWidth) / canvas.width;
    let heightLeft = imageHeight;
    let position = margin;
    const image = canvas.toDataURL("image/png");

    pdf.addImage(image, "PNG", margin, position, imageWidth, imageHeight);
    heightLeft -= pageHeight - margin * 2;

    while (heightLeft > 0) {
      position -= pageHeight - margin * 2;
      pdf.addPage();
      pdf.addImage(image, "PNG", margin, position, imageWidth, imageHeight);
      heightLeft -= pageHeight - margin * 2;
    }

    return pdf;
  };

  const previewReportPdf = async () => {
    const pdf = await buildRecoveryPdf();
    if (pdf) window.open(pdf.output("bloburl"), "_blank");
  };

  const printReportPdf = async () => {
    const pdf = await buildRecoveryPdf();
    if (!pdf) return;
    pdf.autoPrint();
    window.open(pdf.output("bloburl"), "_blank");
  };

  const buildClosurePdf = async () => {
    if (!closureReportRef.current || !closureDetails) return null;
    const canvas = await html2canvas(closureReportRef.current, {
      scale: 2,
      backgroundColor: "#ffffff",
      useCORS: true,
    });
    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 8;
    const imageWidth = pageWidth - margin * 2;
    const imageHeight = (canvas.height * imageWidth) / canvas.width;
    let heightLeft = imageHeight;
    let position = margin;
    const image = canvas.toDataURL("image/png");
    pdf.addImage(image, "PNG", margin, position, imageWidth, imageHeight);
    heightLeft -= pageHeight - margin * 2;
    while (heightLeft > 0) {
      position -= pageHeight - margin * 2;
      pdf.addPage();
      pdf.addImage(image, "PNG", margin, position, imageWidth, imageHeight);
      heightLeft -= pageHeight - margin * 2;
    }
    return pdf;
  };

  const previewClosurePdf = async () => {
    const pdf = await buildClosurePdf();
    if (pdf) window.open(pdf.output("bloburl"), "_blank");
  };

  const printClosurePdf = async () => {
    const pdf = await buildClosurePdf();
    if (!pdf) return;
    pdf.autoPrint();
    window.open(pdf.output("bloburl"), "_blank");
  };

  const validateClosure = async () => {
    if (!closureDetails?.closure) return;
    if (!canValidateClosure) {
      setNotice("Votre profil ne peut pas valider ce rapport.");
      return;
    }
    setLoading(true);
    try {
      await validateDailyClosure(closureDetails.closure.id, { status: "VALIDATED" });
      setNotice("Rapport journalier valide et verse en caisse.");
      setClosureReportOpen(false);
      setClosureDetails(null);
      await load();
    } catch (err) {
      setNotice(err.response?.data?.error || "Impossible de valider le rapport.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="page-content management-page">
      <div className="page-head">
        <div>
          <h1>Cotisations et recouvrement</h1>
          <p>Perception, carte de cotisation, geolocalisation et cloture journaliere.</p>
        </div>
      </div>
      {notice && <p className="notice">{notice}</p>}   

      <section className="panel data-panel">
        <div className="table-toolbar">
          <h2 className="section-title">Rapports journaliers soumis</h2>
          <div className="toolbar-total"><span>Total collecte</span><strong>{money(closureTotal.CDF, "CDF")} / {money(closureTotal.USD, "USD")}</strong></div>
        </div>
        {closures.length ? (
          <table className="data-table">
            <thead><tr><th>Date</th><th>Agent</th><th>Paiements</th><th>CDF</th><th>USD</th><th>Declare</th><th>Ecart</th><th>Statut</th><th>Actions</th></tr></thead>
            <tbody>{closurePagination.rows.map((closure) => (
              <tr key={closure.id}>
                <td>{dateOnly(closure.closure_date)}</td>
                <td>
                  <div className="user-table-cell">
                    {closure.agent_photo_url ? <img src={assetUrl(closure.agent_photo_url)} alt={closure.agent_name} /> : <span>{String(closure.agent_name || "?").slice(0, 1)}</span>}
                    <div>{closure.agent_name}</div>
                  </div>
                </td>
                <td>{closure.payments_count}</td>
                <td>{money(closure.total_collected_cdf, "CDF")}</td>
                <td>{money(closure.total_collected_usd, "USD")}</td>
                <td>{money(closure.total_declared)}</td>
                <td>{money(closure.variance_amount)}</td>
                <td><StatusPill value={closure.status} /></td>
                <td><button className="ghost-btn" type="button" onClick={() => openClosureReport(closure)}><Visibility /> Rapport</button></td>
              </tr>
            ))}</tbody>
          </table>
        ) : <EmptyState label="Aucune cloture soumise." />}
        <Pagination
          page={closurePagination.safePage}
          totalPages={closurePagination.totalPages}
          totalItems={closures.length}
          from={closurePagination.from}
          to={closurePagination.to}
          onPageChange={setClosurePage}
        />
      </section> <br/>
      <section className="panel data-panel">
        <div className="table-toolbar">
          <input placeholder="Recherche avancee..." value={search} onChange={(event) => setSearch(event.target.value)} />
          <div className="period-controls">
            <select value={periodMode} onChange={(event) => setPeriodMode(event.target.value)}>
              <option value="DAY">Jour</option>
              <option value="WEEK">Semaine</option>
              <option value="MONTH">Mois</option>
              <option value="PERIOD">Periode</option>
            </select>
            {periodMode === "PERIOD" && (
              <>
                <input type="date" value={period.start_date} onChange={(event) => setPeriod((current) => ({ ...current, start_date: event.target.value }))} />
                <input type="date" value={period.end_date} onChange={(event) => setPeriod((current) => ({ ...current, end_date: event.target.value }))} />
              </>
            )}
          </div>
          <div className="toolbar-total">
            <span>Total</span>
            <strong>{money(totals.CDF, "CDF")} / {money(totals.USD, "USD")}</strong>
          </div>
          <div className="table-actions">
            {canRecordRecovery && <button className="ghost-btn" type="button" onClick={closeDay}><AssignmentTurnedIn /> Cloturer</button>}
            {canRecordRecovery && <button className="primary-btn" type="button" onClick={() => setModalOpen(true)}><Add /> Ajouter</button>}
          </div>
        </div>
        {filtered.length ? (
          <table className="data-table">
            <thead><tr><th>Date</th><th>Client</th><th>ID carte</th><th>Montant</th><th>Agent</th><th>Position</th></tr></thead>
            <tbody>{pagination.rows.map((recovery) => (
              <tr key={recovery.id}>
                <td>{dateOnly(recovery.paid_at)}</td>
                <td>{recovery.client_name}<br /><small>{recovery.client_number}</small></td>
                <td>{recovery.collection_card_id || recovery.investment_reference}</td>
                <td>{money(recovery.amount_paid, recovery.currency)}</td>
                <td>{recovery.recovery_agent_name}</td>
                <td>{recovery.latitude ? `${recovery.latitude}, ${recovery.longitude}` : "-"}</td>
              </tr>
            ))}</tbody>
          </table>
        ) : <EmptyState />}
        <Pagination
          page={pagination.safePage}
          totalPages={pagination.totalPages}
          totalItems={filtered.length}
          from={pagination.from}
          to={pagination.to}
          onPageChange={setPage}
        />
      </section>
          <br/>

      <section className="panel form-panel">
        <div className="table-toolbar compact-toolbar">
          <h2 className="section-title">Portefeuille par agent</h2>
          <span className="period-chip">Recouvreurs, commerciaux et agents credit</span>
        </div>
        <div className="agent-report-grid">
          {agentPortfolios.map((portfolio) => (
            <article className="agent-report-card agent-person-card" key={portfolio.agent.id}>
              <div className="agent-person-card__photo">
                {portfolio.agent.photo_url ? <img src={assetUrl(portfolio.agent.photo_url)} alt={portfolio.agent.full_name} /> : <span>{String(portfolio.agent.full_name || "?").slice(0, 1)}</span>}
              </div>
              <div className="agent-person-card__body">
                <div>
                  <span>{portfolio.agent.role_label || portfolio.agent.role_code}</span>
                  <h3>{portfolio.agent.full_name}</h3>
                  <small>{portfolio.agent.phone || portfolio.agent.email || "Agent terrain"}</small>
                </div>
                <div className="agent-report-card__metrics">
                  <strong>{portfolio.clients.length}</strong><small>client(s)</small>
                  <strong>{portfolio.investments.length}</strong><small>dossier(s)</small>
                  <strong>{money(portfolio.collectedTotals.CDF, "CDF")}</strong><small>collecte CDF</small>
                  <strong>{money(portfolio.collectedTotals.USD, "USD")}</strong><small>collecte USD</small>
                </div>
                <button className="ghost-btn" type="button" onClick={() => openAgentPortfolio(portfolio)}><Visibility /> Voir clients</button>
              </div>
            </article>
          ))}
        </div>
      </section>
          <br/>

      <section className="panel form-panel">
        <div className="table-toolbar compact-toolbar">
          <h2 className="section-title">Synthese recouvrement par agent</h2>
          <span className="period-chip">{rubricLabel}</span>
        </div>
        <div className="agent-report-grid">
          {agentReports.map((report) => (
            <article className={`agent-report-card agent-person-card ${report.key === "ALL" ? "is-global" : ""}`} key={report.key}>
              <div className="agent-person-card__photo">
                {report.agentPhoto ? <img src={assetUrl(report.agentPhoto)} alt={report.agentName} /> : <span>{report.key === "ALL" ? "T" : String(report.agentName || "?").slice(0, 1)}</span>}
              </div>
              <div className="agent-person-card__body">
                <div>
                  <span>{report.key === "ALL" ? "General" : "Agent"}</span>
                  <h3>{report.agentName}</h3>
                  <small>{report.rows.length} paiement(s) sur la periode</small>
                </div>
              <div className="agent-report-card__metrics">
                <strong>{report.clientsCount}</strong><small>client(s)</small>
                <strong>{money(report.collectedTotals.CDF, "CDF")}</strong><small>CDF encaissé</small>
                <strong>{money(report.collectedTotals.USD, "USD")}</strong><small>USD encaissé</small>
              </div>
              <button className="ghost-btn" type="button" onClick={() => openReport(report)}><Visibility /> Fiche</button>
              </div>
            </article>
          ))}
        </div>
      </section>
          <br/>

      {reportOpen && selectedReport && (
        <Modal
          title={`Fiche de recouvrement - ${selectedReport.agentName}`}
          onClose={() => setReportOpen(false)}
          footer={(
            <>
              <button className="ghost-btn" type="button" onClick={previewReportPdf}><PictureAsPdf /> Visionner PDF</button>
              <button className="primary-btn" type="button" onClick={printReportPdf}><Print /> Imprimer</button>
            </>
          )}
        >
          <div className="report-editor-grid">
            <label>Jour<input value={reportMeta.day} onChange={(event) => setReportMeta((current) => ({ ...current, day: event.target.value }))} /></label>
            <label>Date complet<input value={reportMeta.dateLabel} onChange={(event) => setReportMeta((current) => ({ ...current, dateLabel: event.target.value }))} /></label>
            <label>Rubrique<input value={reportMeta.rubric} onChange={(event) => setReportMeta((current) => ({ ...current, rubric: event.target.value }))} /></label>
            <label>Nom complet de l'agent<input value={reportMeta.agentName} onChange={(event) => setReportMeta((current) => ({ ...current, agentName: event.target.value }))} /></label>
          </div>

          <div className="recovery-report" ref={reportRef}>
            <div className="recovery-agent-head">
              <div className="recovery-agent-photo">
                {selectedReport.agentPhoto ? <img src={assetUrl(selectedReport.agentPhoto)} alt={reportMeta.agentName} /> : <span>{String(reportMeta.agentName || "?").slice(0, 1)}</span>}
              </div>
              <div className="recovery-agent-details">
                <span>OwSur Invest</span>
                <h2>Fiche de recouvrement</h2>
                <p>{reportMeta.rubric}</p>
                <div className="report-stamp">
                  <strong>{reportMeta.day}</strong>
                  <span>{reportMeta.dateLabel}</span>
                </div>
              </div>
            </div>

            <div className="fiche-grid">
              <section>
                <h3>Agent</h3>
                <dl>
                  <dt>Nom complet</dt><dd>{reportMeta.agentName}</dd>
                  <dt>Nombre de client</dt><dd>{selectedReport.clientsCount}</dd>
                  <dt>Total encaissé CDF</dt><dd>{money(selectedReport.collectedTotals.CDF, "CDF")}</dd>
                  <dt>Total encaissé USD</dt><dd>{money(selectedReport.collectedTotals.USD, "USD")}</dd>
                </dl>
              </section>
              <section>
                <h3>Reste à payer</h3>
                <dl>
                  <dt>Total reste CDF</dt><dd>{money(selectedReport.remainingTotals.CDF, "CDF")}</dd>
                  <dt>Total reste USD</dt><dd>{money(selectedReport.remainingTotals.USD, "USD")}</dd>
                  <dt>Rubrique</dt><dd>{reportMeta.rubric}</dd>
                  <dt>Date complet</dt><dd>{reportMeta.dateLabel}</dd>
                </dl>
              </section>
            </div>

            <section className="fiche-section">
              <h3>Clients et paiements</h3>
              {selectedReport.rows.length ? (
                <table className="fiche-table">
                  <thead>
                    <tr>
                      <th>Noms client</th>
                      <th>Montant à payer</th>
                      <th>Reste à payer</th>
                      {periodMode !== "DAY" && <th>Date paiement</th>}
                      <th>Montant encaissé</th>
                    </tr>
                  </thead>
                  <tbody>{selectedReport.rows.map((recovery) => (
                    <tr key={recovery.id}>
                      <td>{recovery.client_name}<br /><small>{recovery.client_number}</small></td>
                      <td>{money(recovery.amount_to_pay, recovery.product_currency || recovery.currency)}</td>
                      <td>{money(recovery.remaining_amount, recovery.product_currency || recovery.currency)}</td>
                      {periodMode !== "DAY" && <td>{dateOnly(recovery.paid_at)}</td>}
                      <td>{money(recovery.amount_paid, recovery.currency)}</td>
                    </tr>
                  ))}</tbody>
                  <tfoot>
                    <tr>
                      <th>Total</th>
                      <th>-</th>
                      <th>{money(selectedReport.remainingTotals.CDF, "CDF")} / {money(selectedReport.remainingTotals.USD, "USD")}</th>
                      {periodMode !== "DAY" && <th>-</th>}
                      <th>{money(selectedReport.collectedTotals.CDF, "CDF")} / {money(selectedReport.collectedTotals.USD, "USD")}</th>
                    </tr>
                  </tfoot>
                </table>
              ) : <p>Aucun recouvrement pour cette periode.</p>}
            </section>
          </div>
        </Modal>
      )}

      {closureReportOpen && (
        <Modal
          title={closureDetails?.closure ? `Rapport journalier - ${closureDetails.closure.agent_name}` : "Rapport journalier"}
          onClose={() => { setClosureReportOpen(false); setClosureDetails(null); }}
          footer={closureDetails?.closure && (
            <>
              <button className="ghost-btn" type="button" onClick={previewClosurePdf}><PictureAsPdf /> Visionner PDF</button>
              <button className="ghost-btn" type="button" onClick={printClosurePdf}><Print /> Imprimer</button>
              {canValidateClosure && closureDetails.closure.status !== "VALIDATED" && <button className="primary-btn" type="button" onClick={validateClosure}><AssignmentTurnedIn /> Valider et verser</button>}
            </>
          )}
        >
          {!closureDetails?.closure ? (
            <EmptyState label="Chargement du rapport..." />
          ) : (
            <div className="recovery-report" ref={closureReportRef}>
              <div className="recovery-agent-head">
                <div className="recovery-agent-photo">
                  {closureDetails.closure.agent_photo_url ? <img src={assetUrl(closureDetails.closure.agent_photo_url)} alt={closureDetails.closure.agent_name} /> : <span>{String(closureDetails.closure.agent_name || "?").slice(0, 1)}</span>}
                </div>
                <div className="recovery-agent-details">
                  <span>OwSur Invest</span>
                  <h2>Rapport journalier de recouvrement</h2>
                  <p>{dateOnly(closureDetails.closure.closure_date)} - {closureDetails.closure.agent_name}</p>
                  <div className="report-stamp">
                    <strong>{closureDetails.closure.status}</strong>
                    <span>{closureDetails.closure.validated_by_name ? `Valide par ${closureDetails.closure.validated_by_name}` : "En attente manager"}</span>
                  </div>
                </div>
              </div>

              <div className="fiche-grid">
                {["CDF", "USD"].map((currency) => (
                  <section key={currency}>
                    <h3>Total {currency}</h3>
                    <dl>
                      <dt>Clients recouvres</dt><dd>{closureDetails.totals?.[currency]?.count || 0}</dd>
                      <dt>Total a verser</dt><dd>{money(closureDetails.totals?.[currency]?.collected, currency)}</dd>
                      <dt>Reste a payer</dt><dd>{money(closureDetails.totals?.[currency]?.remaining, currency)}</dd>
                      <dt>Cotisation prevue</dt><dd>{money(closureDetails.totals?.[currency]?.expected, currency)}</dd>
                    </dl>
                  </section>
                ))}
              </div>

              <section className="fiche-section">
                <h3>Clients recouvres</h3>
                {closureDetails.rows?.length ? (
                  <table className="fiche-table">
                    <thead>
                      <tr>
                        <th>Client</th>
                        <th>Produit</th>
                        <th>Cotisation</th>
                        <th>Montant recu</th>
                        <th>Reste</th>
                      </tr>
                    </thead>
                    <tbody>{closureDetails.rows.map((row) => (
                      <tr key={row.id}>
                        <td>{row.client_name}<br /><small>{row.client_number}</small></td>
                        <td>{row.product_name}<br /><small>{row.collection_card_id || row.investment_reference}</small></td>
                        <td>{money(row.amount_to_pay, row.product_currency || row.currency)}</td>
                        <td>{money(row.amount_paid, row.currency)}</td>
                        <td>{money(row.remaining_amount, row.product_currency || row.currency)}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                ) : <EmptyState label="Aucun client recouvre dans ce rapport." />}
              </section>
            </div>
          )}
        </Modal>
      )}

      {selectedAgentPortfolio && (
        <Modal
          title={`Clients affectes - ${selectedAgentPortfolio.agent.full_name}`}
          onClose={() => { setSelectedAgentPortfolio(null); setSelectedClientFiche(null); }}
        >
          <div className="agent-client-layout">
            <section className="agent-client-list">
              <h3>{selectedAgentPortfolio.clients.length} client(s)</h3>
              {selectedAgentPortfolio.clients.length ? selectedAgentPortfolio.clients.map((client) => (
                <button type="button" key={client.id} onClick={() => openClientFiche(client)}>
                  <strong>{client.full_name}</strong>
                  <span>{client.client_number || client.generated_client_id || client.phone}</span>
                </button>
              )) : <EmptyState label="Aucun client affecte a cet agent." />}
            </section>

            <section className="agent-client-fiche">
              {!selectedClientFiche?.client ? (
                <EmptyState label="Selectionnez un client pour voir sa fiche." />
              ) : (
                <>
                  <div className="fiche-head compact">
                    <div>
                      <span>Fiche client</span>
                      <h2>{selectedClientFiche.client.full_name}</h2>
                      <p>{selectedClientFiche.client.client_number || selectedClientFiche.client.generated_client_id}</p>
                    </div>
                  </div>
                  <div className="fiche-grid">
                    <section>
                      <h3>Identification</h3>
                      <dl>
                        <dt>Telephone</dt><dd>{selectedClientFiche.client.phone || "-"}</dd>
                        <dt>Adresse</dt><dd>{selectedClientFiche.client.address || "-"} {selectedClientFiche.client.commune || ""}</dd>
                        <dt>Agent commercial</dt><dd>{selectedClientFiche.client.commercial_agent_name || "-"}</dd>
                        <dt>Score risque</dt><dd>{selectedClientFiche.client.risk_score || 0}</dd>
                      </dl>
                    </section>
                    <section>
                      <h3>Activites</h3>
                      {selectedClientFiche.activities?.length ? (
                        <div className="fiche-tags">{selectedClientFiche.activities.map((activity) => <span key={activity.id}>{activity.activity_name}</span>)}</div>
                      ) : <p>Aucune activite.</p>}
                    </section>
                  </div>
                  <section className="fiche-section">
                    <h3>Investissements</h3>
                    {selectedClientFiche.investments?.length ? (
                      <table className="fiche-table compact">
                        <tbody>{selectedClientFiche.investments.map((investment) => (
                          <tr key={investment.id}>
                            <td>{investment.product_name}</td>
                            <td>{money(investment.amount_invested, investment.currency || "CDF")}</td>
                            <td><StatusPill value={investment.status} /></td>
                          </tr>
                        ))}</tbody>
                      </table>
                    ) : <p>Aucun investissement.</p>}
                  </section>
                  <section className="fiche-section">
                    <h3>Recouvrements et historique</h3>
                    {selectedClientFiche.recoveries?.length ? (
                      <table className="fiche-table compact">
                        <tbody>{selectedClientFiche.recoveries.slice(0, 8).map((recovery) => (
                          <tr key={recovery.id}>
                            <td>{dateOnly(recovery.paid_at)}</td>
                            <td>{money(recovery.amount_paid, recovery.currency || "CDF")}</td>
                            <td>{recovery.recovery_agent_name || "-"}</td>
                          </tr>
                        ))}</tbody>
                      </table>
                    ) : <p>Aucun recouvrement.</p>}
                  </section>
                </>
              )}
            </section>
          </div>
        </Modal>
      )}

      {modalOpen && (
        <Modal
          title="Ajouter une cotisation"
          onClose={() => setModalOpen(false)}
          footer={<button className="primary-btn" form="recovery-form"><Save /> Enregistrer</button>}
        >
          <form id="recovery-form" onSubmit={submit}>
            <div className="form-grid">
              <label className="full-row">ID carte / souscription<select value={form.investment_id} onChange={(e) => selectRecoveryInvestment(e.target.value)} required><option value="">Selectionner</option>{recoveryInvestments.map((investment) => <option value={investment.id} key={investment.id}>{investment.collection_card_id || investment.reference} - {investment.client_name} - cotisation {money(investment.expected_contribution_amount || investment.amount_to_pay, investment.currency || "CDF")}</option>)}</select></label>
              <label>Montant paye<input type="number" value={form.amount_paid} onChange={(e) => setForm({ ...form, amount_paid: e.target.value })} required /></label>
              <label>Compte caisse<select value={form.cash_account_id} onChange={(e) => setForm({ ...form, cash_account_id: e.target.value })}><option value="">Non renseigne</option>{accounts.map((account) => <option value={account.id} key={account.id}>{account.name}</option>)}</select></label>
              <label>Latitude<input value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} /></label>
              <label>Longitude<input value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} /></label>
              <label className="full-row">Observation<textarea value={form.observation} onChange={(e) => setForm({ ...form, observation: e.target.value })} /></label>
            </div>
            <div className="button-row">
              <button className="ghost-btn" type="button" onClick={locate}><LocationOn /> Position actuelle</button>
            </div>
          </form>
        </Modal>
      )}
      <LoadingOverlay show={loading} label="Traitement en cours..." />
    </main>
  );
}

export default RecoveriesPage;
